import { AxiosError } from 'axios';
import i18n from '@/i18n/config';

/**
 * List of all supported API error codes
 * These match the backend error codes from volovolo-serveur/src/shared/error.ts
 * Translations are in messages/{locale}.json under the "errors" key
 */
export const API_ERROR_CODES = [
  // Generic HTTP errors
  '400', '401', '403', '404', '500', '639', '640',
  // General errors (9xxx)
  '9991', '9992', '9993', '9994',
  // User errors (40xxx, 50xxx)
  '40001', '40002', '40003', '40004',
  '50002', '50003', '50004', '50005', '50006', '50007', '50008', '50009',
  '50010', '50011', '50012', '50013', '50014',
  // Customer/Notification errors (10xxx)
  '10001', '10002', '10003', '10004', '10005',
  '10010', '10011', '10012', '10014', '10015', '10016',
  // Subscription errors (11xxx)
  '11001', '11002', '11003', '11004', '11005', '11006', '11007', '11008',
  // Plan errors (12xxx)
  '12001', '12002', '12003', '12004', '12005', '12006', '12007', '12008',
  // Employee errors (13xxx)
  '13001', '13002', '13003', '13004', '13005', '13006', '13007', '13008', '13009', '13010',
  // Country errors (14xxx)
  '14001', '14002', '14003', '14004', '14005', '14006', '14007',
  // Shipping/Delivery errors (15xxx)
  '15003', '15004', '15011', '15013', '15014', '15021', '15023', '15031', '15033',
  '15041', '15043', '15044', '15046', '15053', '15060', '15061', '15101', '15102', '15103',
  // Metrics errors (16xxx)
  '16001', '16002', '16003', '16004', '16005', '16006', '16007',
  // Advertising errors (17xxx)
  '17001', '17002', '17003', '17004', '17005', '17006', '17007', '17008', '17009',
  '17010', '17011', '17012', '17013', '17014', '17015', '17016', '17017', '17018',
  '17019', '17020', '17021', '17022', '17023', '17024', '17025', '17026',
  // Payment provider errors (20xxx)
  '20001', '20002', '20003', '20004', '20005', '20006', '20007', '20008', '20009',
  '20010', '20011', '20020', '20021',
  // Shop errors (60xxx)
  '60001', '60002', '60003', '60030', '60031', '60032', '60033', '60034', '60036', '60037', '60038',
  // Product errors (70xxx)
  '70030', '70031', '70032', '70033', '70034', '70035', '70036', '70037',
  // Order errors (80xxx)
  '80001', '80002', '80003', '80004', '80005', '80006', '80007', '80010', '80011', '80012',
  '80020', '80021', '80022', '80023',
  // Cart & Payment errors (90xxx)
  '90001', '90002', '90003', '90004', '90005', '90006', '90007', '90008', '90009',
  '90010', '90011', '90012', '90013', '90014', '90015', '90016', '90017', '90018', '90019',
  // Miscellaneous errors (6xx, 7xx)
  '634', '635', '636', '637', '638', '641', '736', '737',
] as const;

/**
 * Check if an error code has a translation
 */
function hasTranslation(code: string): boolean {
  return API_ERROR_CODES.includes(code as typeof API_ERROR_CODES[number]);
}

/**
 * Get translated error message for a code
 */
function getTranslatedError(code: string): string {
  return i18n.t(`errors.${code}`);
}

/**
 * Extract error code and message from an API error response
 */
export function getApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const responseData = error.response?.data as {
      statusCode?: string;
      message?: string;
    } | undefined;

    // Check for specific error code
    const errorCode = responseData?.statusCode;
    if (errorCode && hasTranslation(errorCode)) {
      return getTranslatedError(errorCode);
    }

    // Check for message from server that might be an error code
    if (responseData?.message && hasTranslation(responseData.message)) {
      return getTranslatedError(responseData.message);
    }

    // Fallback to HTTP status code
    const status = error.response?.status?.toString();
    if (status && hasTranslation(status)) {
      return getTranslatedError(status);
    }

    // If server sent a message, use it as fallback
    if (responseData?.message) {
      return responseData.message;
    }
  }

  // Generic fallback
  if (error instanceof Error) {
    return error.message;
  }

  return i18n.t('errors.generic');
}

/**
 * Extract detailed error info for debugging
 */
export function getApiErrorDetails(error: unknown): {
  code: string | null;
  message: string;
  details: unknown;
} {
  if (error instanceof AxiosError) {
    const responseData = error.response?.data as {
      statusCode?: string;
      message?: string;
      details?: unknown;
    } | undefined;

    return {
      code: responseData?.statusCode || error.response?.status?.toString() || null,
      message: getApiErrorMessage(error),
      details: responseData?.details || null,
    };
  }

  return {
    code: null,
    message: error instanceof Error ? error.message : 'Unknown error',
    details: null,
  };
}
